// src/pages/UnifiedPlayer/UnifiedPlayerPage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSponsorPreroll } from '../../hooks/useSponsorPreroll';
import { useHlsPlayback } from '../../hooks/useHlsPlayback';
import { useWatchProgress } from '../../hooks/useWatchProgress';
import { toTagList } from '../../utils/tags';
import ContentCredits from '../../components/Content/ContentCredits';
import {
  ArrowLeft,
  Heart,
  Bookmark,
  Share2,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  SkipBack,
  SkipForward,
  User,
  Calendar,
  Clock,
  Tag,
  Eye,
  Settings,
  Film,
  Mic,
  List
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store';
import { uiActions } from '../../store/reducers/uiReducers';
import MainLayout from '../../components/MainLayout/MainLayout';
// import { SecureMediaPlayer } from '../../components/SecureMedia';
import { config } from '../../config';
import unifiedAuth from '../../utils/unifiedAuth';
import { getFullAvatarUrl, getFullMediaUrl, formatTime, completionPercent } from './playerHelpers';
import CommentsSection from './CommentsSection';

// Type definitions
type ContentType = 'cinema' | 'podcast' | 'animation' | 'sneak-peek' | 'content';



// Import all service types and services
import mediaService from '../../services/mediaService';
import podcastService from '../../services/podcastService';
import animationService from '../../services/animationService';
import sneakPeekService from '../../services/sneakPeekService';
import seriesService from '../../services/seriesService';
import ContentNotFound from '../../components/Common/ContentNotFound';
import LoginPromptModal from '../../components/Auth/LoginPromptModal';
import PreviewLoginWall from '../../components/Auth/PreviewLoginWall';
import type { Season, Episode } from '../../types/series';

// Unified content interface
export interface UnifiedContent {
  /**
   * How the backend is delivering this: an HLS manifest or a single file.
   *
   * Stated rather than inferred from the URL — a signed manifest carries a query
   * string, and guessing from the extension then fails in a way that looks like the
   * video being broken.
   */
  deliveryFormat?: 'HLS' | 'PROGRESSIVE';
  id: string;
  title: string;
  description?: string;
  slug?: string;
  type: 'cinema' | 'content' | 'podcast' | 'animation' | 'sneak-peek';
  category?: string;
  tags?: string[];
  thumbnail?: string;
  poster?: string;
  banner?: string;
  videoUrl?: string;
  audioUrl?: string;
  secureVideoUrl?: string;  
  secureThumbnailUrl?: string;
  securePosterUrl?: string;
  secureBannerUrl?: string;
  duration: number; // in seconds
  views: number;
  likes: number;
  author: {
    id: string;
    firstName?: string;
    lastName?: string;
    username?: string;
    fullName?: string;
    avatar?: string;
    avatarUrl?: string;
  };
  createdAt?: string;
  publishedAt?: string;
  isLiked?: boolean;
  isBookmarked?: boolean;
  watchProgress?: number;
  // Podcast specific
  hasVideo?: boolean;
  series?: {
    id: string;
    title: string;
  };
  episodeNumber?: number;
  seasonNumber?: number;
  // Series information when playing from a series
  seriesInfo?: {
    seriesId: string;
    seriesTitle: string;
    episodeNumber: number;
    seasonNumber: number;
    totalEpisodes: number;
  };
}

// Helper functions moved to playerHelpers.ts (imported above)

const UnifiedPlayerPage: React.FC = () => {
  const { type, id, slug } = useParams<{ type?: string; id?: string; slug?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine content type and ID from URL path
  const getContentTypeAndId = useCallback(() => {
    const pathname = location.pathname;
    
    // Handle both singular and plural routes
    if (pathname.includes('/series/')) {
      // Series URLs - the contentType will be determined from the series type later
      return { contentType: 'series', contentId: id };
    } else if (pathname.includes('/podcast/') || pathname.includes('/podcasts/')) {
      return { contentType: 'podcast', contentId: id };
    } else if (pathname.includes('/animation/') || pathname.includes('/animations/')) {
      return { contentType: 'animation', contentId: id };
    } else if (pathname.includes('/sneak-peek/')) {
      return { contentType: 'sneak-peek', contentId: slug };
    } else if (pathname.includes('/sneak-peeks/')) {
      return { contentType: 'sneak-peek', contentId: id };
    } else if (pathname.includes('/media/cinema/')) {
      return { contentType: 'cinema', contentId: id };
    } else if (pathname.includes('/cinema/')) {
      return { contentType: 'cinema', contentId: id };
    } else if (pathname.includes('/media/contents/')) {
      return { contentType: 'content', contentId: id };
    } else if (pathname.includes('/content/')) {
      return { contentType: 'content', contentId: id };
    } else if (type && id) {
      // /watch/:type/:id format
      if (type === 'series') {
        return { contentType: 'series', contentId: id };
      }
      return { contentType: type, contentId: id };
    }
    
    return { contentType: null, contentId: null };
  }, [location.pathname, type, id, slug]);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const isLoading = useSelector((state: RootState) => state.user.isLoading);
  const dispatch = useDispatch();

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const currentTimeRef = useRef<number>(0);
  const durationRef = useRef<number>(0);
  const lastTrackTimeRef = useRef<number>(0);
  const seriesLoadingRef = useRef<boolean>(false);
  const lastFetchedSeriesIdRef = useRef<string | null>(null);
  const contentLoadingRef = useRef<boolean>(false);
  const viewRecordingInProgressRef = useRef<boolean>(false);
  const recordedViewContentIdRef = useRef<string | null>(null);
  const lastProcessedContentIdRef = useRef<string | null>(null);

  // Preview mode state for unauthenticated users
  const [previewExpired, setPreviewExpired] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptMessage, setLoginPromptMessage] = useState('');
  const previewTimerRef = useRef<NodeJS.Timeout | null>(null);
  const PREVIEW_DURATION = 90; // seconds

  const isAuthenticated = !!userInfo;

  // Determine if this content type allows unlimited free viewing (sneak-peeks)
  const isSneakPeek = useCallback(() => {
    const pathname = location.pathname;
    return pathname.includes('/sneak-peek') || pathname.includes('/sneak-peeks');
  }, [location.pathname]);

  // No hard redirect — unauthenticated users get preview mode

  // States
  const [content, setContent] = useState<UnifiedContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Player state
  const [isPlaying, setIsPlaying] = useState(false);

  // Debug log for isPlaying changes - removed for production
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  // Comments state/handlers moved to CommentsSection.tsx

  // Series state
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number>(1);
  const [selectedEpisode, setSelectedEpisode] = useState<number>(1);
  const [seriesLoading, setSeriesLoading] = useState(false);

  // View tracking state - persistent across page refreshes
  const [viewId, setViewId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('unifiedPlayer_viewId') || null;
    }
    return null;
  });
  const [viewStartTime, setViewStartTime] = useState<number | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('unifiedPlayer_viewStartTime');
      return stored ? parseInt(stored, 10) : null;
    }
    return null;
  });
  const [hasRecordedView, setHasRecordedView] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('unifiedPlayer_hasRecordedView') === 'true';
    }
    return false;
  });

  // 90-second preview timer for unauthenticated users
  // Sneak-peeks are exempt — they play fully without login
  // Skip while auth is still loading to avoid false countdown for logged-in users
  useEffect(() => {
    if (isAuthenticated || isSneakPeek() || previewExpired || isLoading) return;

    // Start timer when video is playing
    if (isPlaying && !previewTimerRef.current) {
      const elapsed = currentTime; // use actual playback position
      const remaining = Math.max(0, PREVIEW_DURATION - elapsed);

      if (remaining <= 0) {
        // Already past preview limit
        setPreviewExpired(true);
        const mediaEl = content?.type === 'podcast' && !content.hasVideo
          ? audioRef.current
          : videoRef.current;
        if (mediaEl) mediaEl.pause();
        return;
      }

      previewTimerRef.current = setTimeout(() => {
        setPreviewExpired(true);
        const mediaEl = content?.type === 'podcast' && !content.hasVideo
          ? audioRef.current
          : videoRef.current;
        if (mediaEl) mediaEl.pause();
        previewTimerRef.current = null;
      }, remaining * 1000);
    }

    // Clear timer when paused
    if (!isPlaying && previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    };
  }, [isPlaying, isAuthenticated, isSneakPeek, previewExpired, currentTime, content, PREVIEW_DURATION, isLoading]);

  // Also enforce the limit on every time update (handles seeking past 90s)
  useEffect(() => {
    if (isAuthenticated || isSneakPeek() || previewExpired || isLoading) return;

    if (currentTime >= PREVIEW_DURATION) {
      setPreviewExpired(true);
      const mediaEl = content?.type === 'podcast' && !content.hasVideo
        ? audioRef.current
        : videoRef.current;
      if (mediaEl) mediaEl.pause();
    }
  }, [currentTime, isAuthenticated, isSneakPeek, previewExpired, content, PREVIEW_DURATION, isLoading]);

  // Helper to require auth for interactions
  const requireAuth = useCallback((action: string): boolean => {
    if (isAuthenticated) return true;
    setLoginPromptMessage(`Sign in to ${action}.`);
    setShowLoginPrompt(true);
    return false;
  }, [isAuthenticated]);

  // Normalize content data from different services
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizeContentData = useCallback((rawContent: any, contentType: string): UnifiedContent => {
    const baseContent = {
      id: rawContent.id,
      // 'HLS' or 'PROGRESSIVE', straight from the backend. Not inferred from the file
      // extension: a signed manifest carries a query string, and guessing then fails
      // in a way that looks like the video being broken rather than like a bad guess.
      deliveryFormat: rawContent.deliveryFormat as 'HLS' | 'PROGRESSIVE' | undefined,
      title: rawContent.title,
      description: rawContent.description || rawContent.short_description || '',
      type: contentType as ContentType,
      duration: rawContent.duration || rawContent.durationSeconds || 0,
      views: (() => {
        // Backend consistently returns viewCount, but handle legacy fields for safety
        const viewCount = rawContent.viewCount || 
                         rawContent.view_count || 
                         rawContent.views || 
                         0;
        return viewCount;
      })(),
      likes: rawContent.likeCount || rawContent.like_count || rawContent.likes || 0,
      createdAt: rawContent.createdAt || rawContent.created_at,
      publishedAt: rawContent.publishedAt || rawContent.published_at,
      isLiked: rawContent.isLiked || rawContent.is_liked || false,
      isBookmarked: rawContent.isBookmarked || rawContent.is_bookmarked || false,
      watchProgress: rawContent.watchProgress || rawContent.watch_progress || 0,
      // Default author structure
      author: {
        id: rawContent.author?.id || rawContent.creatorId || 'unknown',
        firstName: rawContent.author?.firstName || rawContent.author?.first_name,
        lastName: rawContent.author?.lastName || rawContent.author?.last_name,
        username: rawContent.author?.username,
        fullName: rawContent.author?.fullName || rawContent.creatorName,
        avatar: rawContent.author?.avatar,
        avatarUrl: rawContent.author?.avatarUrl,
      },
      // Add series information if available
      ...(rawContent.seriesInfo && {
        seriesInfo: rawContent.seriesInfo
      })
    };

    // Handle different content types
    switch (contentType) {
      case 'cinema':
      case 'content':
        return {
          ...baseContent,
          slug: rawContent.slug,
          category: rawContent.category,
          tags: toTagList(rawContent.tags),
          thumbnail: rawContent.thumbnail || rawContent.thumbnailUrl,
          poster: rawContent.poster,
          banner: rawContent.banner,
          videoUrl: rawContent.video_file || rawContent.videoUrl || rawContent.mediaUrl,
          secureVideoUrl: rawContent.secure_video_url,
          secureThumbnailUrl: rawContent.secure_thumbnail_url,
          securePosterUrl: rawContent.secure_poster_url,
          secureBannerUrl: rawContent.secure_banner_url,
        };

      case 'podcast': {
        // Check if the mediaUrl contains video content by examining file extension or content type
        const mediaUrl = rawContent.mediaUrl || rawContent.audioFile || rawContent.audio_file || rawContent.audioUrl || rawContent.videoFile || rawContent.video_file || rawContent.videoUrl;
        const isVideoContent = mediaUrl ? (
          mediaUrl.toLowerCase().includes('.mp4') ||
          mediaUrl.toLowerCase().includes('.webm') ||
          mediaUrl.toLowerCase().includes('.avi') ||
          mediaUrl.toLowerCase().includes('.mov') ||
          mediaUrl.toLowerCase().includes('.mkv') ||
          mediaUrl.toLowerCase().includes('.m4v') ||
          // An HLS manifest is video. Without this a migrated film falls through to
          // the audio branch and renders as a podcast with no picture.
          mediaUrl.toLowerCase().includes('.m3u8') ||
          rawContent.deliveryFormat === 'HLS' ||
          rawContent.contentType?.startsWith('video/') ||
          !!rawContent.videoFile || !!rawContent.video_file || !!rawContent.videoUrl
        ) : false;
        
        return {
          ...baseContent,
          slug: rawContent.slug,
          category: rawContent.category,
          tags: toTagList(rawContent.tags),
          thumbnail: rawContent.thumbnail || rawContent.thumbnailUrl,
          audioUrl: !isVideoContent ? mediaUrl : undefined,
          videoUrl: isVideoContent ? mediaUrl : undefined,
          hasVideo: isVideoContent,
          secureVideoUrl: rawContent.secure_video_url,
          secureThumbnailUrl: rawContent.secure_thumbnail_url,
          series: rawContent.series ? {
            id: rawContent.series.id,
            title: rawContent.series.title
          } : undefined,
          episodeNumber: rawContent.episodeNumber || rawContent.episode_number,
          seasonNumber: rawContent.seasonNumber || rawContent.season_number,
        };
      }

      case 'animation':
        return {
          ...baseContent,
          slug: rawContent.slug,
          category: rawContent.category,
          tags: toTagList(rawContent.tags),
          thumbnail: rawContent.thumbnail,
          poster: rawContent.poster,
          banner: rawContent.banner,
          videoUrl: rawContent.video_file,
          secureVideoUrl: rawContent.secure_video_url,
          secureThumbnailUrl: rawContent.secure_thumbnail_url,
          securePosterUrl: rawContent.secure_poster_url,
          secureBannerUrl: rawContent.secure_banner_url,
        };

      case 'sneak-peek':
        return {
          ...baseContent,
          slug: rawContent.slug,
          category: rawContent.category,
          tags: toTagList(rawContent.tags),
          thumbnail: rawContent.thumbnail || rawContent.thumbnailUrl,
          videoUrl: rawContent.video_file || rawContent.videoUrl || rawContent.mediaUrl,
          secureVideoUrl: rawContent.secure_video_url,
          secureThumbnailUrl: rawContent.secure_thumbnail_url,
        };

      default:
        return baseContent;
    }
  }, []);

  // Fetch content based on type
  const fetchContent = useCallback(async () => {
    const { contentType, contentId } = getContentTypeAndId();
    
    if (!contentType || !contentId || contentId === 'undefined') {
      // Only set error if the current pathname looks like a content route.
      // During route transitions (e.g. navigating to /login), the URL may
      // briefly resolve here with null values — avoid triggering state
      // updates that could interfere with the navigation.
      const pathname = location.pathname;
      const isContentRoute = /^\/(watch|cinema|content|podcast|animation|sneak-peek|media|series)/.test(pathname);
      if (isContentRoute) {
        setError(`Invalid content URL. Content type: ${contentType}, Content ID: ${contentId}`);
        setLoading(false);
      }
      return;
    }

    // Prevent duplicate fetches
    if (contentLoadingRef.current) return;

    try {
      contentLoadingRef.current = true;
      setLoading(true);
      setError(null);
      
      // Reset series tracking refs when fetching new content
      lastFetchedSeriesIdRef.current = null;
      seriesLoadingRef.current = false;

      let rawContent;
      let actualContentType = contentType; // Track the actual content type for normalization

      // Determine if we should expect a series based on the URL path and contentType
      const pathname = location.pathname;
      const expectSeries = contentType === 'series' || 
                          pathname.includes('/series/') || 
                          pathname.includes('/season/') || 
                          pathname.includes('/episode/');

      let isSeries = false;
      
      // Only check if it's a series when the URL suggests it might be
      if (expectSeries) {
        try {
          const series = await seriesService.getSeriesById(contentId);
          if (series && series.id) {
            isSeries = true;
            // Series identified successfully
          }
        } catch (_error) {
          // Not a series, continue with regular content fetching
          isSeries = false;
        }
      } else {
        // For regular content URLs, skip the series check entirely
        // URL indicates regular content, skipping series check
      }

      if (isSeries) {
        try {
          // This is a series - load the series and its first episode
          const series = await seriesService.getSeriesById(contentId);
          
          // Get all episodes for this series
          const episodes = await seriesService.getAllEpisodesBySeries(contentId);
          
          if (episodes && episodes.length > 0) {
          // Get the first episode
          const firstEpisode = episodes[0];
          
          // Debug: Log the episode structure to understand the data - removed for production
          
          // Get the content ID from the episode structure with multiple fallback attempts
          const episodeData = firstEpisode as Episode & { 
            content?: { id: string }; 
            contentId?: string; 
          };
          const episodeContentId = episodeData.content?.id || 
                                  episodeData.contentId || 
                                  firstEpisode.id;
          
          // Episode content ID extraction debug - removed for production
          
          if (!episodeContentId) {
            throw new Error(`Episode "${firstEpisode.title}" does not have valid content associated with it. Episode ID: ${firstEpisode.id}. Please check the series configuration.`);
          }
          
          // Determine the correct content type based on series type
          switch (series.seriesType) {
            case 'CINEMA_SERIES':
              actualContentType = 'cinema';
              break;
            case 'CONTENT_SERIES':
              actualContentType = 'content';
              break;
            case 'PODCAST_SERIES':
              actualContentType = 'podcast';
              break;
            case 'ANIMATION_SERIES':
              actualContentType = 'animation';
              break;
            default:
              actualContentType = 'content'; // fallback
          }
          
          // Loading episode content debug - removed for production
          
          try {
            // Try to fetch the episode content with improved error handling
            let episodeContent = null;
            
            // Try all content types if the series type mapping fails
            const contentTypesToTry = [actualContentType];
            
            // Add fallback content types if not already included
            if (!contentTypesToTry.includes('content')) contentTypesToTry.push('content');
            if (!contentTypesToTry.includes('cinema')) contentTypesToTry.push('cinema');
            if (!contentTypesToTry.includes('podcast')) contentTypesToTry.push('podcast');
            if (!contentTypesToTry.includes('animation')) contentTypesToTry.push('animation');
            
            let lastError: Error | null = null;
            
            for (const contentType of contentTypesToTry) {
              try {
                // Attempting to fetch episode content
                
                switch (contentType) {
                  case 'cinema':
                    episodeContent = await mediaService.getCinemaItem(episodeContentId);
                    actualContentType = 'cinema';
                    break;
                  case 'content':
                    episodeContent = await mediaService.getContentItem(episodeContentId);
                    actualContentType = 'content';
                    break;
                  case 'podcast':
                    episodeContent = await podcastService.getPodcast(episodeContentId);
                    actualContentType = 'podcast';
                    break;
                  case 'animation':
                    episodeContent = await animationService.getAnimation(episodeContentId);
                    actualContentType = 'animation';
                    break;
                  case 'sneak-peek':
                    episodeContent = await sneakPeekService.getSneakPeek(episodeContentId);
                    actualContentType = 'sneak-peek';
                    break;
                  default:
                    continue; // Skip unknown content types
                }
                
                if (episodeContent) {
                  // Successfully loaded episode content
                  break; // Success, exit the loop
                }
              } catch (error) {
                // Failed to fetch episode content
                lastError = error instanceof Error ? error : new Error(String(error));
                episodeContent = null; // Reset for next attempt
              }
            }
            
            // If we successfully get episode content, use it
            if (episodeContent) {
              // Cast to any to add seriesInfo property
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const contentWithSeries = episodeContent as any;
              
              // Override the title to show series title instead of episode title
              const seriesTitle = series.title;
              contentWithSeries.title = seriesTitle;
              
              contentWithSeries.seriesInfo = {
                seriesId: series.id,
                seriesTitle: seriesTitle,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                episodeNumber: (firstEpisode as any).episodeNumber || (firstEpisode as any).episode_number || 1,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                seasonNumber: (firstEpisode as any).seasonNumber || (firstEpisode as any).season_number || 1,
                totalEpisodes: episodes.length
              };
              
              rawContent = contentWithSeries;
              
              // Fetch and set series data for the UI
              fetchSeriesData(series.id);
            } else {
              throw new Error(`Episode content with ID "${episodeContentId}" not found. Last error: ${lastError?.message || 'Unknown error'}`);
            }
          } catch (episodeError) {
            console.error('Failed to load episode content:', episodeError);
            
            // Provide more detailed error message
            const errorMessage = episodeError instanceof Error ? episodeError.message : 'Unknown error';
            
            if (errorMessage.includes('not found') || errorMessage.includes('404')) {
              throw new Error(
                `Episode Content Missing\n\nThe first episode of "${series.title}" references content that doesn't exist.\n\nEpisode: "${firstEpisode.title}"\nContent ID: ${episodeContentId}\nSeries Type: ${series.seriesType}\n\nThis might be a configuration issue with the series setup. Please contact support.`
              );
            } else if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
              throw new Error(
                `Server Error Loading Episode\n\nThere was a server error while loading the first episode of "${series.title}".\n\nEpisode: "${firstEpisode.title}"\nContent ID: ${episodeContentId}\n\nPlease try again later or contact support if the problem persists.`
              );
            } else {
              throw new Error(
                `Unable to Load Episode Content\n\nFailed to load the first episode of "${series.title}".\n\nEpisode: "${firstEpisode.title}"\nError: ${errorMessage}\n\nPlease try refreshing the page or contact support if the problem persists.`
              );
            }
          }
        } else {
          // Series has no episodes - try to treat the ID as regular content instead
          // Series has no episodes, falling back to regular content
          isSeries = false;
        }
        } catch (_seriesError) {
          // Error loading series, falling back to regular content
          isSeries = false;
        }
      }
      
      if (!isSeries) {
        // This is regular content - fetch it normally
        // If contentType was 'series' but we determined it's not actually a series,
        // we need to detect the actual content type by trying different APIs
        
        if (contentType === 'series') {
          // Try to determine the actual content type by testing different APIs
          // Fallback from series: detecting actual content type
          
          try {
            // Try content first (most common)
            // Trying getContentItem
            rawContent = await mediaService.getContentItem(contentId);
            actualContentType = 'content';
          } catch (_contentError) {
            // getContentItem failed, trying getCinemaItem
            try {
              // Try cinema
              rawContent = await mediaService.getCinemaItem(contentId);
              actualContentType = 'cinema';
            } catch (_cinemaError) {
              // getCinemaItem failed, trying getPodcast
              try {
                // Try podcast
                rawContent = await podcastService.getPodcast(contentId);
                actualContentType = 'podcast';
              } catch {
                try {
                  // Try animation
                  rawContent = await animationService.getAnimation(contentId);
                  actualContentType = 'animation';
                } catch {
                  try {
                    // Try sneak-peek
                    rawContent = await sneakPeekService.getSneakPeek(contentId);
                    actualContentType = 'sneak-peek';
                  } catch {
                    throw new Error(`Unable to determine content type for ID: ${contentId}`);
                  }
                }
              }
            }
          }
        } else {
          // Normal case - we know the content type
          switch (contentType) {
            case 'cinema':
              rawContent = await mediaService.getCinemaItem(contentId);
              break;
            case 'content':
              rawContent = await mediaService.getContentItem(contentId);
              break;
            case 'podcast':
              rawContent = await podcastService.getPodcast(contentId);
              break;
            case 'animation':
              rawContent = await animationService.getAnimation(contentId);
              break;
            case 'sneak-peek':
              rawContent = await sneakPeekService.getSneakPeek(contentId);
              break;
            default:
              throw new Error(`Unsupported content type: ${contentType}`);
          }
          actualContentType = contentType;
        }
      }

      if (!rawContent) {
        throw new Error('Content not found');
      }

      // For series content, we use the actualContentType which was determined from the series type
      
      const normalizedContent = normalizeContentData(rawContent, actualContentType);
      
      // For regular content, check if it's part of a series (only if not already a series)
      if (!isSeries && !expectSeries) {
        try {
          const seriesInfo = await seriesService.getContentSeriesInfo(contentId);
          if (seriesInfo && seriesInfo.series) {
            // Content is part of a series, adding series context
            normalizedContent.seriesInfo = {
              seriesId: seriesInfo.series.id,
              seriesTitle: seriesInfo.series.title,
              episodeNumber: seriesInfo.episodeNumber || seriesInfo.episode?.episodeNumber || 1,
              seasonNumber: seriesInfo.seasonNumber || seriesInfo.season?.seasonNumber || 1,
              totalEpisodes: seriesInfo.series.totalEpisodes || 0
            };
            
            // Fetch series data for the UI
            fetchSeriesData(seriesInfo.series.id);
          }
        } catch (_seriesInfoErr) {
          // Content is not part of any series, which is fine
        }
      }
      
      // Fetch user interactions for authenticated users (use the actual content id)
      try {
        const interactionTargetId = normalizedContent.id || contentId;
        if (interactionTargetId && userInfo) {
          const userInteractions = await mediaService.getUserInteractions(interactionTargetId);
          normalizedContent.isLiked = userInteractions.liked;
          normalizedContent.isBookmarked = userInteractions.bookmarked;
        }
      } catch (_interactionErr) {
        // Could not fetch user interactions (user may not be authenticated)
        // Keep default values from normalizeContentData
      }
      
      setContent(normalizedContent);

    } catch (err) {
      console.error('Error fetching content:', err);
      
      let errorMessage = 'Failed to load content';
      const { contentType, contentId } = getContentTypeAndId();
      
      if (err instanceof Error) {
        if (err.message.includes('not found') || err.message.includes('404') || err.message.includes('Content not found')) {
          errorMessage = `Content Not Found\n\nThe ${contentType} content you're looking for doesn't exist or may have been removed.\n\nContent ID: ${contentId}\n\nThis might happen if:\n• The content was deleted\n• You followed an invalid link\n• There was an error in content navigation\n\nTry going back to browse other content.`;
        } else if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (err.message.includes('An unexpected error occurred')) {
          errorMessage = `Content Not Found\n\nAn unexpected error occurred. Please try again later.\n\nContent Type: ${contentType}\nContent ID: ${contentId}`;
        } else {
          errorMessage = `${err.message}\n\nContent Type: ${contentType}\nContent ID: ${contentId}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      contentLoadingRef.current = false;
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContentTypeAndId, normalizeContentData]);

  // Fetch series data (seasons and episodes)
  const fetchSeriesData = useCallback(async (seriesId: string) => {
    if (!seriesId) return;
    
    // Use a ref to track if we're already loading to prevent duplicate calls
    if (seriesLoadingRef.current) return;
    
    // Check if we already fetched this series
    if (lastFetchedSeriesIdRef.current === seriesId) return;
    
    seriesLoadingRef.current = true;
    lastFetchedSeriesIdRef.current = seriesId;
    setSeriesLoading(true);
    try {
      // Fetch seasons and episodes in parallel
      const [seasonsData, episodesData] = await Promise.all([
        seriesService.getSeasonsBySeries(seriesId),
        seriesService.getAllEpisodesBySeries(seriesId)
      ]);
      
      setSeasons(seasonsData);
      setEpisodes(episodesData);
      
      // Set initial selected season and episode based on current content
      if (content?.seriesInfo) {
        setSelectedSeason(content.seriesInfo.seasonNumber);
        setSelectedEpisode(content.seriesInfo.episodeNumber);
      } else if (seasonsData.length > 0 && episodesData.length > 0) {
        setSelectedSeason(seasonsData[0].seasonNumber);
        setSelectedEpisode(episodesData[0].episodeNumber);
      }
    } catch (error) {
      console.error('Error fetching series data:', error);
    } finally {
      seriesLoadingRef.current = false;
      setSeriesLoading(false);
    }
  }, [content?.seriesInfo]);

  // Persistent state helpers
  const setViewIdPersistent = useCallback((id: string | null) => {
    setViewId(id);
    if (typeof window !== 'undefined') {
      if (id) {
        sessionStorage.setItem('unifiedPlayer_viewId', id);
      } else {
        sessionStorage.removeItem('unifiedPlayer_viewId');
      }
    }
  }, []);

  const setViewStartTimePersistent = useCallback((time: number | null) => {
    setViewStartTime(time);
    if (typeof window !== 'undefined') {
      if (time) {
        sessionStorage.setItem('unifiedPlayer_viewStartTime', time.toString());
      } else {
        sessionStorage.removeItem('unifiedPlayer_viewStartTime');
      }
    }
  }, []);

  const setHasRecordedViewPersistent = useCallback((recorded: boolean) => {
    setHasRecordedView(recorded);
    if (typeof window !== 'undefined') {
      if (recorded) {
        sessionStorage.setItem('unifiedPlayer_hasRecordedView', 'true');
      } else {
        sessionStorage.removeItem('unifiedPlayer_hasRecordedView');
      }
    }
  }, []);

  // View tracking functions
  const recordView = useCallback(async () => {
    if (!content || !content.id || hasRecordedView) {
      return null;
    }

    // Double-check session storage to prevent race conditions
    if (typeof window !== 'undefined') {
      const storedHasRecorded = sessionStorage.getItem('unifiedPlayer_hasRecordedView') === 'true';
      const storedContentId = sessionStorage.getItem('unifiedPlayer_contentId');
      if (storedHasRecorded && storedContentId === content.id) {
        setHasRecordedViewPersistent(true);
        return null;
      }
    }

    try {
      const response = await mediaService.recordView(content.id);
      
      setViewIdPersistent(response.viewId);
      setViewStartTimePersistent(Date.now());
      setHasRecordedViewPersistent(true);
      recordedViewContentIdRef.current = content.id;
      
      // View recorded successfully - update the content view count locally and then fetch updated count
      setContent(prevContent => {
        if (prevContent) {
          const newViews = prevContent.views + 1;
          return {
            ...prevContent,
            views: newViews
          };
        }
        return prevContent;
      });

      // After a longer delay, verify the view count from backend to ensure accuracy
      // Use a longer delay to allow backend database transactions to complete
      setTimeout(async () => {
        if (content?.id) {
          try {
            // Use the unified content endpoint for consistency.
            // Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
            const response = await unifiedAuth.fetch(`${config.apiBaseUrl}/content/${content.id}`, {
              headers: {
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const apiResponse = await response.json();
              const backendViewCount = apiResponse.data?.viewCount || apiResponse.data?.views || 0;
              
              
              // Sync with backend as source of truth, but avoid unnecessary updates
              setContent(prevContent => {
                if (prevContent && backendViewCount !== prevContent.views) {
                  return {
                    ...prevContent,
                    views: backendViewCount
                  };
                }
                return prevContent;
              });
            }
          } catch (error) {
            console.warn('Failed to verify view count from backend:', error);
          }
        }
      }, 3000); // Wait 3 seconds for backend to fully process
      
      return response.viewId;
    } catch (error) {
      console.error('Failed to record view:', error);
      return null;
    }
  }, [content, hasRecordedView, setViewIdPersistent, setViewStartTimePersistent, setHasRecordedViewPersistent]);

  /**
   * How far through the film this viewer actually is.
   *
   * This used to be computed as `(Date.now() / 1000) / (content.duration * 60) * 100`,
   * clamped to 100. Two errors on top of each other: `currentTime` was assigned
   * `Date.now()` - shadowing the name the player uses for its own position - so the
   * numerator was Unix seconds, about 1.79 billion; and it multiplied the duration by 60
   * on the belief that `content.duration` was minutes, while the same field is read as
   * seconds two thousand lines further down. Every row that reported came out at 100%.
   *
   * It was invisible because `content.duration` was 0 for everything - the encoder's
   * length was never stored - so the branch never ran. Storing the real duration is what
   * makes this matter.
   *
   * `playedSeconds` is the position in the film. `wallClockSeconds` is time with the page
   * open, which is a different thing and is what view_duration has always meant: it counts
   * pauses and idle tabs, so the two are not interchangeable.
   */
  const updateViewProgress = useCallback(async () => {
    if (!viewId || !viewStartTime || !content) return;

    const wallClockSeconds = Math.floor((Date.now() - viewStartTime) / 1000);

    // The refs directly rather than getPlaybackElement, which is declared further down -
    // naming it in this dependency array would read it before initialisation and throw on
    // every render. Same choice it makes: audio only for a podcast with no video.
    const element: HTMLMediaElement | null =
      content.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    const playedSeconds = element?.currentTime ?? currentTime;
    // The element's own duration is authoritative once media has loaded; content.duration
    // is the stored figure and is in seconds, matching every other reader of that field.
    const totalSeconds = element?.duration && Number.isFinite(element.duration)
      ? element.duration
      : content.duration;

    const completionPercentage = completionPercent(playedSeconds, totalSeconds);

    try {
      await mediaService.updateViewProgress(viewId, wallClockSeconds, completionPercentage);
    } catch (error) {
      console.error('Failed to update view progress:', error);
    }
  }, [viewId, viewStartTime, content, currentTime]);

  // Load content on mount and when params change
  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Fetch series data when content is loaded and has series info
  useEffect(() => {
    const seriesId = content?.seriesInfo?.seriesId;
    if (seriesId && lastFetchedSeriesIdRef.current !== seriesId) {
      fetchSeriesData(seriesId);
    }
  }, [content?.seriesInfo?.seriesId, fetchSeriesData]);

  // Record view when content starts playing
  useEffect(() => {
    
    // Check if we should record a view
    const shouldRecord = isPlaying && 
                        content && 
                        content.id && 
                        !hasRecordedView && 
                        recordedViewContentIdRef.current !== content.id;
    
    if (shouldRecord && !viewRecordingInProgressRef.current) {
      viewRecordingInProgressRef.current = true;
      
      // Add a small delay to prevent accidental views from quick clicks
      const timer = setTimeout(async () => {
        // Double-check conditions before recording
        if (!hasRecordedView && content?.id && recordedViewContentIdRef.current !== content.id) {
          try {
            await recordView();
            recordedViewContentIdRef.current = content.id;
          } catch (error) {
            console.error('Failed to record view:', error);
          }
        } 
        // Always reset the flag after the timer completes
        viewRecordingInProgressRef.current = false;
      }, 5000); // Record view after 5 seconds of playing

      return () => {
        clearTimeout(timer);
        viewRecordingInProgressRef.current = false;
      };
    }
  }, [isPlaying, content, hasRecordedView, recordView]);

  // Update view progress periodically while playing
  useEffect(() => {
    if (isPlaying && viewId) {
      const interval = setInterval(() => {
        updateViewProgress();
      }, 30000); // Update every 30 seconds

      return () => clearInterval(interval);
    }
  }, [isPlaying, viewId, updateViewProgress]);

  // Reset view tracking when content changes - use ref to prevent reset loops
  useEffect(() => {
    if (typeof window !== 'undefined' && content?.id && lastProcessedContentIdRef.current !== content.id) {
      const storedContentId = sessionStorage.getItem('unifiedPlayer_contentId');
      
      if (storedContentId !== content.id) {
        // Content actually changed, reset view tracking
        setViewIdPersistent(null);
        setViewStartTimePersistent(null);
        setHasRecordedViewPersistent(false);
        viewRecordingInProgressRef.current = false;
        recordedViewContentIdRef.current = null;
        sessionStorage.setItem('unifiedPlayer_contentId', content.id);
      } else if (storedContentId === content.id) {
        // Same content loaded after refresh - restore view tracking state
        const storedViewId = sessionStorage.getItem('unifiedPlayer_viewId');
        const storedViewStartTime = sessionStorage.getItem('unifiedPlayer_viewStartTime');
        const storedHasRecordedView = sessionStorage.getItem('unifiedPlayer_hasRecordedView');
        
        
        
        // Check if the stored data is still valid (not too old, e.g., more than 2 hours)
        if (storedViewStartTime) {
          const storedStartTime = parseInt(storedViewStartTime, 10);
          const currentTime = Date.now();
          const timeDiff = currentTime - storedStartTime;
          const twoHours = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
          
          if (timeDiff > twoHours) {
            // Data is too old, clear it
            setViewIdPersistent(null);
            setViewStartTimePersistent(null);
            setHasRecordedViewPersistent(false);
          } else {
            // Data is valid, restore it
            if (storedViewId) {
              setViewIdPersistent(storedViewId);
            }
            if (storedStartTime) {
              setViewStartTimePersistent(storedStartTime);
            }
            if (storedHasRecordedView === 'true') {
              setHasRecordedViewPersistent(true);
            }
          }
        }
      }
      
      // Mark this content as processed to prevent re-processing
      lastProcessedContentIdRef.current = content.id;
    }
  }, [content?.id, setViewIdPersistent, setViewStartTimePersistent, setHasRecordedViewPersistent]);

  // Update view progress when component unmounts or content changes
  useEffect(() => {
    return () => {
      if (viewId) {
        updateViewProgress();
      }
      // Clean up session storage when component unmounts (user navigates away)
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('unifiedPlayer_viewId');
        sessionStorage.removeItem('unifiedPlayer_viewStartTime');
        sessionStorage.removeItem('unifiedPlayer_hasRecordedView');
        sessionStorage.removeItem('unifiedPlayer_contentId');
      }
      // Reset refs
      viewRecordingInProgressRef.current = false;
      recordedViewContentIdRef.current = null;
    };
  }, [viewId, updateViewProgress]);

  // Handle episode selection
  const handleEpisodeSelect = useCallback(async (seasonNumber: number, episodeNumber: number) => {
    if (!content?.seriesInfo?.seriesId) {
      console.warn('No series ID available for episode selection');
      return;
    }
    
    setLoading(true);
    setError(null); // Clear any previous errors
    
    try {
      // Find the selected episode
      const selectedEpisodeData = episodes.find(ep => 
        ep.season?.seasonNumber === seasonNumber && ep.episodeNumber === episodeNumber
      );
      
      if (!selectedEpisodeData) {
        console.error('Episode not found:', { seasonNumber, episodeNumber });
        // Available episodes debug - removed for production
        
        setError(`Episode ${seasonNumber}x${episodeNumber} not found in this series.`);
        return;
      }
      
      // Get episode content ID with fallbacks
      const episodeData = selectedEpisodeData as Episode & { 
        content?: { id: string }; 
        contentId?: string; 
      };
      const episodeContentId = episodeData.content?.id || 
                              episodeData.contentId || 
                              selectedEpisodeData.id;
      
      // Episode selection debug - removed for production
      
      if (!episodeContentId) {
        throw new Error(`Episode "${selectedEpisodeData.title}" (S${seasonNumber}E${episodeNumber}) does not have valid content associated with it.`);
      }
      
      // Determine content type from series info or current content
      const { contentType } = getContentTypeAndId();
      let actualContentType = contentType;
      
      // Use series type to determine content type if available
      if (content.seriesInfo) {
        // Convert series type to content type
        const seriesTypeMap: Record<string, string> = {
          'CINEMA_SERIES': 'cinema',
          'CONTENT_SERIES': 'content', 
          'PODCAST_SERIES': 'podcast',
          'ANIMATION_SERIES': 'animation'
        };
        
        // Get series info to determine the correct content type
        try {
          const seriesData = await seriesService.getSeriesById(content.seriesInfo.seriesId);
          if (seriesData?.seriesType && seriesTypeMap[seriesData.seriesType]) {
            actualContentType = seriesTypeMap[seriesData.seriesType];
          }
        } catch (seriesError) {
          console.warn('Could not fetch series data for content type detection:', seriesError);
        }
      }
      
      // Try to fetch episode content with fallback content types
      let episodeContent = null;
      const contentTypesToTry = [actualContentType];
      
      // Add fallback content types if not already included
      if (!contentTypesToTry.includes('content')) contentTypesToTry.push('content');
      if (!contentTypesToTry.includes('cinema')) contentTypesToTry.push('cinema');
      if (!contentTypesToTry.includes('podcast')) contentTypesToTry.push('podcast');
      if (!contentTypesToTry.includes('animation')) contentTypesToTry.push('animation');
      
      let lastError = null;
      
      for (const tryContentType of contentTypesToTry) {
        try {
          // Attempting to fetch episode as specific content type
          
          switch (tryContentType) {
            case 'cinema':
              episodeContent = await mediaService.getCinemaItem(episodeContentId);
              actualContentType = 'cinema';
              break;
            case 'content':
              episodeContent = await mediaService.getContentItem(episodeContentId);
              actualContentType = 'content';
              break;
            case 'podcast':
              episodeContent = await podcastService.getPodcast(episodeContentId);
              actualContentType = 'podcast';
              break;
            case 'animation':
              episodeContent = await animationService.getAnimation(episodeContentId);
              actualContentType = 'animation';
              break;
            case 'sneak-peek':
              episodeContent = await sneakPeekService.getSneakPeek(episodeContentId);
              actualContentType = 'sneak-peek';
              break;
            default:
              continue; // Skip unknown content types
          }
          
          if (episodeContent) {
            // Successfully loaded episode
            break; // Success, exit the loop
          }
        } catch (error) {
          // Failed to fetch episode as specific content type
          lastError = error;
          episodeContent = null; // Reset for next attempt
        }
      }
      
      if (episodeContent) {
        // Cast to any to add seriesInfo property
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const contentWithSeries = episodeContent as any;
        
        // Keep the series title instead of episode title
        contentWithSeries.title = content.seriesInfo.seriesTitle;
        
        contentWithSeries.seriesInfo = {
          seriesId: content.seriesInfo.seriesId,
          seriesTitle: content.seriesInfo.seriesTitle,
          episodeNumber: selectedEpisodeData.episodeNumber,
          seasonNumber: selectedEpisodeData.season?.seasonNumber || seasonNumber,
          totalEpisodes: episodes.length
        };
        
        const normalizedContent = normalizeContentData(contentWithSeries, actualContentType || 'content');
        setContent(normalizedContent);
        setSelectedSeason(seasonNumber);
        setSelectedEpisode(episodeNumber);
        
        // Reset player state
        setCurrentTime(0);
        setIsPlaying(false);
        
        // Reset view tracking for new episode
        setViewIdPersistent(null);
        setViewStartTimePersistent(null);
        setHasRecordedViewPersistent(false);
        viewRecordingInProgressRef.current = false;
        recordedViewContentIdRef.current = null;
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('unifiedPlayer_contentId', normalizedContent.id);
        }
      } else {
        const errorMsg = lastError instanceof Error ? lastError.message : 'Unknown error';
        throw new Error(`Could not load episode content: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Error switching episode:', error);
      
      let errorMessage = `Failed to load episode S${seasonNumber}E${episodeNumber}`;
      
      if (error instanceof Error) {
        if (error.message.includes('not found') || error.message.includes('404')) {
          errorMessage = `Episode S${seasonNumber}E${episodeNumber} content not found. The episode may have been removed or there may be a configuration issue.`;
        } else if (error.message.includes('does not have valid content')) {
          errorMessage = `Episode S${seasonNumber}E${episodeNumber} is not properly configured. Please contact support.`;
        } else {
          errorMessage = `Failed to load episode S${seasonNumber}E${episodeNumber}: ${error.message}`;
        }
      }
      
      setError(errorMessage);
      
      // Don't change the current episode selection on error
      console.warn('Keeping current episode selection due to error');
    } finally {
      setLoading(false);
    }
  }, [content?.seriesInfo, episodes, getContentTypeAndId, normalizeContentData, setViewIdPersistent, setViewStartTimePersistent, setHasRecordedViewPersistent]);

  // Comment fetching/state/handlers moved to CommentsSection.tsx

  // Track watch progress
  const trackWatchProgress = useCallback(async () => {
    if (!content || !userInfo) return;

    try {
      // Watch progress tracking can be implemented later based on available service methods
      // const progress = duration > 0 ? currentTimeRef.current / durationRef.current : 0;
      // Implementation would go here
    } catch (err) {
      console.error('Error tracking watch progress:', err);
    }
  }, [content, userInfo]);

  // Player control functions
  // Sponsor pre-roll. Replaces the Google IMA integration that used to live here:
  // a partner sends a video file and it plays before the content, with no ad server
  // and no third-party SDK. Does nothing at all unless a sponsor is configured.
  const {
    creative: sponsor,
    sponsorVideoRef,
    isSponsorPlaying,
    canSkip: canSkipSponsor,
    secondsUntilSkip,
    playIfPresent: playSponsorIfPresent,
    handleSponsorTimeUpdate,
    handleSponsorEnded,
    handleSponsorError,
    skipSponsor,
  } = useSponsorPreroll({
    // No sponsor ahead of a sneak peek - the same exclusion the old ad integration
    // applied, for the same reason: a teaser that opens with an advert is worse
    // than no teaser.
    enabled: !isSneakPeek(),
    onFinished: () => {
      videoRef.current?.play().catch(() => {});
      setIsPlaying(true);
    },
  });

  const handlePlayPause = useCallback(() => {
    if (previewExpired) return; // blocked by the login wall
    const mediaElement = content?.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    if (!mediaElement) return;

    if (isPlaying) {
      mediaElement.pause();
      setIsPlaying(false);
      return;
    }

    // The sponsor takes the first play, once. It calls back when it ends or is
    // skipped, and every failure path in it calls back too - so the content always
    // starts, even if the sponsor video is broken or autoplay is refused.
    if (playSponsorIfPresent()) return;

    mediaElement.play().catch(() => {});
    setIsPlaying(true);
  }, [isPlaying, content, previewExpired, playSponsorIfPresent]);

  const handleSeek = useCallback((time: number) => {

    const mediaElement = content?.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    if (!mediaElement) return;

    // Prevent unauthenticated users from seeking past preview limit
    if (!isAuthenticated && !isSneakPeek() && time >= PREVIEW_DURATION) {
      time = PREVIEW_DURATION - 1;
    }

    mediaElement.currentTime = time;
    setCurrentTime(time);
    currentTimeRef.current = time;
  }, [content, isAuthenticated, isSneakPeek, PREVIEW_DURATION]);

  const handleVolumeChange = useCallback((newVolume: number) => {
    const mediaElement = content?.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    if (!mediaElement) return;

    mediaElement.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  }, [content]);

  const handleMuteToggle = useCallback(() => {
    const mediaElement = content?.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    if (!mediaElement) return;

    const newMuted = !isMuted;
    mediaElement.muted = newMuted;
    setIsMuted(newMuted);
  }, [isMuted, content]);

  const skipTime = useCallback((seconds: number) => {
    const mediaElement = content?.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    if (!mediaElement) return;

    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    handleSeek(newTime);
  }, [currentTime, duration, handleSeek, content]);

  const toggleFullscreen = useCallback(() => {
    const mediaElement = content?.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    if (!mediaElement) return;

    if (!isFullscreen) {
      // Try to enter fullscreen on the video/audio element's parent container
      const playerContainer = document.getElementById('player-container');
      if (playerContainer) {
        if (playerContainer.requestFullscreen) {
          playerContainer.requestFullscreen();
        }
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, [isFullscreen, content]);

  const handlePlaybackSpeedChange = useCallback((speed: number) => {
    const mediaElement = content?.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    if (!mediaElement) return;

    mediaElement.playbackRate = speed;
    setPlaybackSpeed(speed);
    setShowSettings(false);
  }, [content]);

  // Media event handlers
  const handleTimeUpdate = useCallback(() => {
    const mediaElement = content?.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    if (!mediaElement) return;

    const time = mediaElement.currentTime;
    setCurrentTime(time);
    currentTimeRef.current = time;

    // Check for video ad breaks
    if (mediaElement === videoRef.current) {
    }

    // Track watch progress periodically (every 10 seconds)
    if (time - lastTrackTimeRef.current >= 10) {
      trackWatchProgress();
      lastTrackTimeRef.current = time;
    }
  }, [content, trackWatchProgress]);

  const handleLoadedMetadata = useCallback(() => {
    const mediaElement = content?.type === 'podcast' && !content.hasVideo ? audioRef.current : videoRef.current;
    if (!mediaElement) return;

    const mediaDuration = mediaElement.duration;
    setDuration(mediaDuration);
    durationRef.current = mediaDuration;

    // Set initial playback speed
    mediaElement.playbackRate = playbackSpeed;

    // Initialize IMA video ads once we know the duration
    if (mediaElement === videoRef.current && videoRef.current) {
    }

  }, [content, handleSeek, playbackSpeed]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    trackWatchProgress();
  }, [trackWatchProgress]);

  // Interaction handlers
  const handleLike = useCallback(async () => {
    if (!content) return;
    if (!requireAuth('like this content')) return;

    try {
      // Call the appropriate backend API based on content type
      switch (content.type) {
        case 'cinema':
          await mediaService.interactWithCinema(content.id, {
            interaction_type: 'like'
          });
          break;
        case 'content':
          await mediaService.interactWithContent(content.id, {
            interactionType: 'like'
          });
          break;
        case 'podcast':
          await mediaService.interactWithContent(content.id, {
            interactionType: 'like'
          });
          break;
        case 'animation':
          await animationService.interactWithAnimation(content.id, 'like');
          break;
        case 'sneak-peek':
          await sneakPeekService.toggleLike(content.id);
          break;
        default:
          throw new Error(`Like interaction not supported for content type: ${content.type}`);
      }

      // Update local state after successful API call
      setContent(prev => prev ? {
        ...prev,
        isLiked: !prev.isLiked,
        likes: prev.isLiked ? prev.likes - 1 : prev.likes + 1
      } : null);

      dispatch(uiActions.addNotification({
        message: content.isLiked ? 'Removed from likes' : 'Added to likes',
        type: 'success'
      }));
    } catch (err) {
      console.error('Error toggling like:', err);
      dispatch(uiActions.addNotification({
        message: 'Failed to update like status. Please try again.',
        type: 'error'
      }));
    }
  }, [content, requireAuth, dispatch]);

  const handleBookmark = useCallback(async () => {
    if (!content) return;
    if (!requireAuth('bookmark this content')) return;

    try {
      // Call the appropriate backend API based on content type
      switch (content.type) {
        case 'cinema':
          await mediaService.interactWithCinema(content.id, {
            interaction_type: 'bookmark'
          });
          break;
        case 'content':
          await mediaService.interactWithContent(content.id, {
            interactionType: 'bookmark'
          });
          break;
        case 'podcast':
          await mediaService.interactWithContent(content.id, {
            interactionType: 'bookmark'
          });
          break;
        case 'animation':
          await animationService.interactWithAnimation(content.id, 'bookmark');
          break;
        case 'sneak-peek':
          await sneakPeekService.toggleBookmark(content.id);
          break;
        default:
          throw new Error(`Bookmark interaction not supported for content type: ${content.type}`);
      }

      // Update local state after successful API call
      setContent(prev => prev ? {
        ...prev,
        isBookmarked: !prev.isBookmarked
      } : null);

      dispatch(uiActions.addNotification({
        message: content.isBookmarked ? 'Removed from bookmarks' : 'Added to bookmarks',
        type: 'success'
      }));
    } catch (err) {
      console.error('Error toggling bookmark:', err);
      dispatch(uiActions.addNotification({
        message: 'Failed to update bookmark status. Please try again.',
        type: 'error'
      }));
    }
  }, [content, requireAuth, dispatch]);

  const handleShare = useCallback(async () => {
    if (!content) return;

    const shareData = {
      title: content.title,
      text: content.description,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled sharing
      }
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href);
      dispatch(uiActions.addNotification({
        message: 'Link copied to clipboard!',
        type: 'success'
      }));
    }
  }, [content, dispatch]);


  // Track when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (userInfo && content) {
        trackWatchProgress();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (userInfo && content) {
        trackWatchProgress();
      }
    };
  }, [userInfo, content, trackWatchProgress]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return; // Don't handle shortcuts when typing
      }

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skipTime(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skipTime(10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          handleVolumeChange(Math.min(1, volume + 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          handleVolumeChange(Math.max(0, volume - 0.1));
          break;
        case 'm':
          e.preventDefault();
          handleMuteToggle();
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlePlayPause, skipTime, handleVolumeChange, handleMuteToggle, volume]);

  // Handle mouse activity for control visibility
  useEffect(() => {
    let timeoutId: number;

    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeoutId);
      if (isPlaying) {
        timeoutId = setTimeout(() => setShowControls(false), 3000);
      }
    };

    const handleMouseLeave = () => {
      if (isPlaying) {
        setShowControls(false);
      }
    };

    const playerContainer = document.getElementById('player-container');
    if (playerContainer) {
      playerContainer.addEventListener('mousemove', handleMouseMove);
      playerContainer.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (playerContainer) {
        playerContainer.removeEventListener('mousemove', handleMouseMove);
        playerContainer.removeEventListener('mouseleave', handleMouseLeave);
      }
      clearTimeout(timeoutId);
    };
  }, [isPlaying]);

  // Handle fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Close settings when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const settingsButton = document.getElementById('settings-button');
      const settingsMenu = document.getElementById('settings-menu');
      const audioSettingsButton = document.getElementById('audio-settings-button');
      const audioSettingsMenu = document.getElementById('audio-settings-menu');
      
      if (showSettings) {
        const isClickInsideVideoSettings = settingsButton?.contains(event.target as Node) || 
                                           settingsMenu?.contains(event.target as Node);
        const isClickInsideAudioSettings = audioSettingsButton?.contains(event.target as Node) || 
                                            audioSettingsMenu?.contains(event.target as Node);
        
        if (!isClickInsideVideoSettings && !isClickInsideAudioSettings) {
          setShowSettings(false);
        }
      }
    };

    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings]);

  // Loading state — only gate on content loading, not auth loading.
  // Auth state (isLoading) should not block the player since unauthenticated
  // users get a 90-second preview. Gating on isLoading caused a race condition
  // where the page showed "Loading content..." until auth finished initializing,
  // requiring multiple refreshes on production.
  // Called before any early return, because hooks must run in the same order on
  // every render and there are `if (loading)` / `if (!content)` returns below. The
  // URL is computed inline because the helper that used to do it lived below those
  // returns and assumed content was non-null.
  const playbackUrl = content
    ? (content.secureVideoUrl || getFullMediaUrl(content.videoUrl))
    : undefined;

  // useCallback so the effect does not re-run on every render and tear down a
  // playing video just to rebuild it.
  const handlePlaybackError = useCallback((message: string) => {
    setError(message);
  }, []);

  useHlsPlayback(videoRef, playbackUrl, content?.deliveryFormat, handlePlaybackError);

  // Resume where a signed-in viewer stopped, and keep saving where they are. Replaces a
  // block that resumed from content.watchProgress - a field the backend never returned,
  // so it never once ran. Above the early returns for the same reason as the hook above.
  const getPlaybackElement = useCallback(
    (): HTMLMediaElement | null =>
      content?.type === 'podcast' && !content?.hasVideo ? audioRef.current : videoRef.current,
    [content?.type, content?.hasVideo],
  );
  const resume = useWatchProgress(getPlaybackElement, content?.id, isAuthenticated);

  // The resume notice is useful for a few seconds, then it is clutter over the film.
  useEffect(() => {
    if (resume.resumedFrom == null) return;
    const t = window.setTimeout(resume.dismiss, 12000);
    return () => window.clearTimeout(t);
  }, [resume.resumedFrom, resume.dismiss]);

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400 text-lg">Loading content...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Error state
  if (error || !content) {
    // Check if this is a series error
    const isSeriesError = error?.includes('🎬 This appears to be a series');
    
    if (isSeriesError) {
      // Special handling for series error - keeping the original layout
      return (
        <MainLayout>
          <div className="min-h-screen bg-gray-900 flex items-center justify-center">
            <div className="text-center max-w-2xl mx-4">
              <div className="text-6xl mb-6">🎬</div>
              <h2 className="text-2xl font-bold text-white mb-4">This is a Series</h2>
              <div className="text-left bg-gray-800 rounded-lg p-6 mb-6">
                <p className="text-gray-300 mb-4">
                  Series cannot be played directly. You need to select a specific episode to watch.
                </p>
                <div className="space-y-2 text-sm text-gray-400">
                  <p><strong>Please follow these steps:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Navigate back to the series page</li>
                    <li>Browse the available episodes</li>
                    <li>Select a specific episode to watch</li>
                  </ul>
                </div>
                {/* Extract Series ID from error message */}
                {error?.includes('Series ID:') && (
                  <div className="mt-4 p-3 bg-gray-700 rounded text-xs">
                    <p className="text-gray-400">
                      {error.split('💡 ')[1]?.split('If this should be')[0]?.trim()}
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate('/originals')}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Browse Series
              </button>
            </div>
          </div>
        </MainLayout>
      );
    }

    // For regular content not found errors, use the new component
    const { contentType, contentId } = getContentTypeAndId();
    
    const handleRetry = () => {
      setError(null);
      setLoading(true);
      fetchContent();
    };

    return (
      <ContentNotFound
        contentType={contentType || undefined}
        contentId={contentId || undefined}
        error={error || 'The content you\'re looking for doesn\'t exist.'}
        onRetry={handleRetry}
      />
    );
  }

  // Get media URLs
  // getVideoUrl() used to live here. It is gone rather than left unused: the same
  // value is now computed as `playbackUrl` above the early returns, because the
  // playback hook must run before them, and two functions returning the same URL
  // from different places is how they drift apart.

  const getAudioUrl = () => {
    return getFullMediaUrl(content.audioUrl);
  };

  // Attaches the source to the <video> element. For a progressive file this is the
  // same as setting src; for an HLS manifest it wires up hls.js, which Chrome and
  // Firefox need and Safari does not.

  const getThumbnailUrl = () => {
    return content.secureThumbnailUrl || getFullMediaUrl(content.thumbnail);
  };

  const getPosterUrl = () => {
    return content.securePosterUrl || getFullMediaUrl(content.poster);
  };

  // Determine player type
  const isPodcastAudioOnly = content.type === 'podcast' && !content.hasVideo;
  const hasVideo = content.videoUrl || content.secureVideoUrl;
  const hasAudio = content.audioUrl;

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-900 text-white">
        {/* Back Navigation */}
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 pb-8">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            
            {/* Player Section */}
            <div className="xl:col-span-2">
              <div 
                id="player-container"
                className="bg-black rounded-lg overflow-hidden relative group mb-6"
              >
                {isPodcastAudioOnly ? (
                  // Audio-only player for podcasts
                  <div className="aspect-video bg-gradient-to-br from-orange-900 via-blue-900 to-neutral-900 flex items-center justify-center relative">
                    <audio
                      ref={audioRef}
                      className="hidden"
                      src={getAudioUrl()}
                      controls={false}
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      onEnded={handleEnded}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                    />
                    
                    {/* Audio visualizer/artwork */}
                    <div className="text-center">
                      {getThumbnailUrl() && (
                        <img
                          src={getThumbnailUrl()}
                          alt={content.title}
                          className="w-48 h-48 rounded-lg mx-auto mb-6 object-cover shadow-2xl"
                        />
                      )}
                      <div className="flex items-center space-x-4">
                        <Mic className="w-8 h-8 text-orange-400" />
                        <div className="flex space-x-1">
                          {[...Array(8)].map((_, i) => (
                            <div
                              key={i}
                              className={`w-1 bg-orange-500 rounded-full ${
                                isPlaying ? 'animate-pulse' : ''
                              }`}
                              style={{
                                height: `${Math.random() * 40 + 10}px`,
                                animationDelay: `${i * 0.1}s`
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Audio Controls Overlay */}
                    <div 
                      className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${
                        showControls ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        {/* Progress Bar */}
                        <div 
                          ref={progressRef}
                          className="w-full h-2 bg-gray-600 rounded-full cursor-pointer mb-4"
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const pos = (e.clientX - rect.left) / rect.width;
                            handleSeek(pos * duration);
                          }}
                        >
                          <div 
                            className="h-full bg-orange-600 rounded-full relative"
                            style={{ width: `${(currentTime / duration) * 100}%` }}
                          >
                            <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg"></div>
                          </div>
                        </div>

                        {/* Audio Controls */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <button onClick={() => skipTime(-10)} className="text-gray-400 hover:text-white">
                              <SkipBack className="w-6 h-6" />
                            </button>
                            
                            <button 
                              onClick={handlePlayPause}
                              className="bg-orange-600 text-white rounded-full p-4 hover:bg-orange-700 transition-colors"
                            >
                              {isPlaying ? (
                                <Pause className="w-6 h-6" />
                              ) : (
                                <Play className="w-6 h-6" />
                              )}
                            </button>
                            
                            <button onClick={() => skipTime(10)} className="text-gray-400 hover:text-white">
                              <SkipForward className="w-6 h-6" />
                            </button>

                            <button onClick={handleMuteToggle} className="text-gray-400 hover:text-white">
                              {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                            </button>

                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={isMuted ? 0 : volume}
                              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                              className="w-24"
                            />
                          </div>

                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2 text-sm">
                              <span>{formatTime(currentTime)}</span>
                              <span>/</span>
                              <span>{formatTime(duration)}</span>
                            </div>

                            <div className="relative">
                              <button 
                                id="audio-settings-button"
                                onClick={() => setShowSettings(!showSettings)}
                                className="text-gray-400 hover:text-white"
                              >
                                <Settings className="w-5 h-5" />
                              </button>
                              
                              {showSettings && (
                                <div 
                                  id="audio-settings-menu"
                                  className="absolute bottom-full right-0 mb-2 bg-black/90 backdrop-blur-sm rounded-lg p-3 min-w-[150px] z-10"
                                >
                                  <div className="text-white text-sm mb-2 font-medium">Playback Speed</div>
                                  <div className="space-y-1">
                                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                                      <button
                                        key={speed}
                                        onClick={() => handlePlaybackSpeedChange(speed)}
                                        className={`block w-full text-left px-2 py-1 rounded text-sm transition-colors ${
                                          playbackSpeed === speed 
                                            ? 'bg-orange-600 text-white' 
                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                        }`}
                                      >
                                        {speed}x {speed === 1 ? '(Normal)' : ''}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            <button 
                              onClick={toggleFullscreen}
                              className="text-gray-400 hover:text-white"
                            >
                              <Maximize className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Preview login wall for audio player */}
                    <PreviewLoginWall isVisible={previewExpired} />
                  </div>
                ) : hasVideo ? (
                  // Video player
                  <div className="relative aspect-video">
                    {/* Centred over the player, but playback is NOT paused and the film is
                        not covered: the wrapper ignores pointer events so controls stay
                        usable, and it fades itself out. Most people came back to carry on
                        watching, so asking them to choose every time is friction on the
                        common case; the choice is offered rather than demanded. */}
                    {resume.resumedFrom != null && (
                      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
                        <div
                          role="status"
                          className="pointer-events-auto mx-4 max-w-sm rounded-xl bg-black/85 px-6 py-5 text-center text-white shadow-2xl backdrop-blur-sm"
                        >
                          <p className="text-base font-semibold">
                            Resuming from {formatTime(resume.resumedFrom)}
                          </p>
                          <p className="mt-1 text-sm text-gray-300">
                            Picking up where you left off.
                          </p>
                          <div className="mt-4 flex items-center justify-center gap-3">
                            <button
                              type="button"
                              onClick={resume.startOver}
                              className="rounded-lg bg-white/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                            >
                              Start from beginning
                            </button>
                            <button
                              type="button"
                              onClick={resume.dismiss}
                              className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                            >
                              Keep watching
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <video
                      ref={videoRef}
                      className={`w-full h-full object-contain`}
                      poster={getPosterUrl() || getThumbnailUrl()}
                      controls={false}
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      onEnded={handleEnded}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                    />

                    {/* Sponsor pre-roll overlay */}
                    {sponsor && (
                      <div className={`absolute inset-0 bg-black ${isSponsorPlaying ? 'block z-10' : 'hidden'}`}>
                        <video
                          ref={sponsorVideoRef}
                          className="w-full h-full object-contain"
                          src={sponsor.videoUrl}
                          playsInline
                          onTimeUpdate={handleSponsorTimeUpdate}
                          onEnded={handleSponsorEnded}
                          onError={handleSponsorError}
                        />
                        <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-sm">
                          Sponsored
                        </div>
                        {sponsor.clickThroughUrl && (
                          <a
                            href={sponsor.clickThroughUrl}
                            target="_blank"
                            rel="noopener noreferrer sponsored"
                            className="absolute bottom-4 left-4 bg-white/90 text-black px-4 py-2 rounded text-sm font-medium hover:bg-white"
                          >
                            Visit sponsor
                          </a>
                        )}
                        <button
                          onClick={skipSponsor}
                          disabled={!canSkipSponsor}
                          className="absolute bottom-4 right-4 bg-black/70 text-white px-4 py-2 rounded text-sm disabled:opacity-60"
                        >
                          {canSkipSponsor ? 'Skip' : `Skip in ${secondsUntilSkip}s`}
                        </button>
                      </div>
                    )}


                    {/* Ad Playing Indicator */}

                    {/* Video Controls Overlay */}
                    <div 
                      className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${
                        showControls ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        {/* Progress Bar */}
                        <div 
                          className="w-full h-2 bg-gray-600 rounded-full cursor-pointer mb-4 relative"
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const pos = (e.clientX - rect.left) / rect.width;
                            handleSeek(pos * duration);
                          }}
                        >
                          <div 
                            className="h-full bg-orange-600 rounded-full relative"
                            style={{ width: `${(currentTime / duration) * 100}%` }}
                          />
                        </div>

                        {/* Video Controls */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <button onClick={() => skipTime(-10)} className="text-white hover:text-orange-400">
                              <SkipBack className="w-6 h-6" />
                            </button>
                            
                            <button 
                              onClick={handlePlayPause}
                              className="bg-orange-600 hover:bg-orange-700 text-white rounded-full p-3"
                            >
                              {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                            </button>
                            
                            <button onClick={() => skipTime(10)} className="text-white hover:text-orange-400">
                              <SkipForward className="w-6 h-6" />
                            </button>

                            <button onClick={handleMuteToggle} className="text-white hover:text-orange-400">
                              {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                            </button>

                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={isMuted ? 0 : volume}
                              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                              className="w-24"
                            />
                          </div>

                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2 text-sm">
                              <span>{formatTime(currentTime)}</span>
                              <span>/</span>
                              <span>{formatTime(duration)}</span>
                            </div>

                            <div className="relative">
                              <button 
                                id="settings-button"
                                onClick={() => setShowSettings(!showSettings)}
                                className="text-white hover:text-orange-400"
                              >
                                <Settings className="w-5 h-5" />
                              </button>
                              
                              {showSettings && (
                                <div 
                                  id="settings-menu"
                                  className="absolute bottom-full right-0 mb-2 bg-black/90 backdrop-blur-sm rounded-lg p-3 min-w-[150px] z-10"
                                >
                                  <div className="text-white text-sm mb-2 font-medium">Playback Speed</div>
                                  <div className="space-y-1">
                                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                                      <button
                                        key={speed}
                                        onClick={() => handlePlaybackSpeedChange(speed)}
                                        className={`block w-full text-left px-2 py-1 rounded text-sm transition-colors ${
                                          playbackSpeed === speed 
                                            ? 'bg-orange-600 text-white' 
                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                        }`}
                                      >
                                        {speed}x {speed === 1 ? '(Normal)' : ''}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            <button 
                              onClick={toggleFullscreen}
                              className="text-white hover:text-orange-400"
                            >
                              <Maximize className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Center play button */}
                    {!isPlaying && !previewExpired && (
                      <button
                        onClick={handlePlayPause}
                        className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/40 transition-colors"
                      >
                        <div className="bg-white/90 hover:bg-white rounded-full p-6">
                          <Play className="w-12 h-12 text-black ml-1" />
                        </div>
                      </button>
                    )}

                    {/* Preview login wall overlay — shown when 90-sec preview expires */}
                    <PreviewLoginWall isVisible={previewExpired} />
                  </div>
                ) : (
                  // Fallback for content without media
                  <div className="aspect-video bg-gray-800 flex items-center justify-center">
                    <div className="text-center">
                      <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No media available</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Content Information */}
              <div className="bg-gray-900 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-white mb-2">{content.title}</h1>
                    
                    {/* Content type indicator */}
                    <div className="flex items-center space-x-2 mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        content.type === 'cinema' ? 'bg-red-900 text-red-300' :
                        content.type === 'podcast' ? 'bg-green-900 text-green-300' :
                        content.type === 'animation' ? 'bg-blue-900 text-blue-300' :
                        content.type === 'sneak-peek' ? 'bg-yellow-900 text-yellow-300' :
                        'bg-orange-900 text-orange-300'
                      }`}>
                        {content.type.replace('-', ' ').toUpperCase()}
                      </span>
                      
                      {content.category && (
                        <span className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-xs">
                          {content.category}
                        </span>
                      )}
                    </div>

                    {/* Series information banner */}
                    {content.seriesInfo && (
                      <div className="bg-gradient-to-r from-orange-900/50 to-blue-900/50 rounded-lg p-4 mb-4 border border-orange-500/30">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <Film className="w-5 h-5 text-orange-400" />
                            <span className="text-white font-medium">Playing from Series</span>
                          </div>
                          <span className="text-gray-300">•</span>
                          <span className="text-orange-300 font-medium">{content.seriesInfo.seriesTitle}</span>
                        </div>
                        <div className="mt-2 flex items-center space-x-4 text-sm text-gray-300">
                          <span>Season {content.seriesInfo.seasonNumber}</span>
                          <span>•</span>
                          <span>Episode {content.seriesInfo.episodeNumber}</span>
                          <span>•</span>
                          <span>{content.seriesInfo.totalEpisodes} episodes total</span>
                        </div>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="flex items-center space-x-6 text-sm text-gray-400 mb-4">
                      <div className="flex items-center space-x-2">
                        <Eye className="w-4 h-4" />
                        <span>{content.views.toLocaleString()} views</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4" />
                        <span>{formatTime(content.duration)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(content.publishedAt || content.createdAt || Date.now()).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Series info for podcasts */}
                    {content.type === 'podcast' && content.series && (
                      <div className="flex items-center space-x-2 mb-4">
                        <List className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-300">{content.series.title}</span>
                        {content.episodeNumber && (
                          <>
                            <span className="text-gray-500">•</span>
                            <span className="text-gray-400">Episode {content.episodeNumber}</span>
                          </>
                        )}
                        {content.seasonNumber && (
                          <>
                            <span className="text-gray-500">•</span>
                            <span className="text-gray-400">Season {content.seasonNumber}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-4 mb-6">
                  <button
                    onClick={handleLike}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      content.isLiked 
                        ? 'bg-orange-600 text-white' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${content.isLiked ? 'fill-current' : ''}`} />
                    <span>{content.likes}</span>
                  </button>

                  <button
                    onClick={handleBookmark}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      content.isBookmarked 
                        ? 'bg-yellow-600 text-white' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <Bookmark className={`w-5 h-5 ${content.isBookmarked ? 'fill-current' : ''}`} />
                    <span>{content.isBookmarked ? 'Bookmarked' : 'Bookmark'}</span>
                  </button>

                  <button
                    onClick={handleShare}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-800 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                    <span>Share</span>
                  </button>
                </div>

                {/* Description */}
                {content.description && (
                  <div className="prose prose-invert max-w-none">
                    <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {content.description}
                    </p>
                  </div>
                )}

                {/* Who made it. Renders nothing when there are no credits, which is
                    the normal state for most content. */}
                {content.id && <ContentCredits contentId={String(content.id)} />}

                {/* Series Episode Selector */}
                {content.seriesInfo && seasons.length > 0 && episodes.length > 0 && (
                  <div className="mt-6 bg-gray-800 rounded-lg p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <List className="w-5 h-5 text-orange-400" />
                      <h3 className="text-lg font-semibold text-white">Episodes</h3>
                      {seriesLoading && (
                        <div className="text-sm text-gray-400">Loading...</div>
                      )}
                    </div>
                    
                    {/* Season Selector */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Season</label>
                      <select
                        value={selectedSeason}
                        onChange={(e) => {
                          const newSeason = Number(e.target.value);
                          // Season changed
                          setSelectedSeason(newSeason);
                        }}
                        className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        {seasons.map((season) => (
                          <option key={season.id} value={season.seasonNumber}>
                            Season {season.seasonNumber} - {season.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Episode Grid */}
                    <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                      {episodes
                        .filter(episode => episode.season?.seasonNumber === selectedSeason)
                        .sort((a, b) => a.episodeNumber - b.episodeNumber)
                        .map((episode) => (
                          <button
                            key={episode.id}
                            onClick={() => {
                              handleEpisodeSelect(selectedSeason, episode.episodeNumber);
                            }}
                            className={`text-left p-3 rounded-lg transition-colors ${
                              selectedEpisode === episode.episodeNumber && selectedSeason === episode.season?.seasonNumber
                                ? 'bg-orange-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-medium">
                                  Episode {episode.episodeNumber}: {episode.title}
                                </div>
                                {episode.description && (
                                  <div className="text-sm text-gray-100 mt-1 line-clamp-2">
                                    {episode.description}
                                  </div>
                                )}
                                <div className="flex items-center space-x-4 text-xs text-gray-100 mt-2">
                                  <span>{Math.floor(episode.durationSeconds / 60)} min</span>
                                  <span>•</span>
                                  <span>{episode.viewCount.toLocaleString()} views</span>
                                </div>
                              </div>
                              {selectedEpisode === episode.episodeNumber && selectedSeason === episode.season?.seasonNumber && (
                                <Play className="w-5 h-5 text-white ml-3" />
                              )}
                            </div>
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {content.tags && content.tags.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="w-4 h-4 text-gray-400" />
                      {content.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-sm hover:bg-gray-700 cursor-pointer"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1">
              {/* Author Information */}
              <div className="bg-gray-900 rounded-lg p-6 mb-6">
                <div className="flex items-center space-x-4 mb-4">
                  {content.author.avatar || content.author.avatarUrl ? (
                    <img
                      src={getFullAvatarUrl(content.author.avatar || content.author.avatarUrl)}
                      alt={content.author.fullName || content.author.username}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-white">
                      {content.author.fullName || 
                       `${content.author.firstName || ''} ${content.author.lastName || ''}`.trim() ||
                       content.author.username}
                    </h3>
                    <p className="text-sm text-gray-400">Creator</p>
                  </div>
                </div>
              </div>

              {/* Comments Section (extracted to CommentsSection.tsx) */}
              <CommentsSection content={content} requireAuth={requireAuth} />

              {/* Keyboard Shortcuts (for podcasts and videos) */}
              {(hasVideo || hasAudio) && (
                <div className="bg-gray-900 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Keyboard Shortcuts</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Play/Pause</span>
                      <span className="text-gray-300">Space or K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Seek Backward</span>
                      <span className="text-gray-300">← (10s)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Seek Forward</span>
                      <span className="text-gray-300">→ (10s)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Volume Up</span>
                      <span className="text-gray-300">↑</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Volume Down</span>
                      <span className="text-gray-300">↓</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Mute/Unmute</span>
                      <span className="text-gray-300">M</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Fullscreen</span>
                      <span className="text-gray-300">F</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Login prompt modal for like/comment/bookmark actions */}
        <LoginPromptModal
          isOpen={showLoginPrompt}
          onClose={() => setShowLoginPrompt(false)}
          message={loginPromptMessage}
        />
      </div>
    </MainLayout>
  );
};

export default UnifiedPlayerPage;